import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Target, Clock, AlertTriangle, Server, Shield, Activity, Trash2,
  RefreshCcw, CheckCircle, XCircle, Info, Wifi, Router, Camera, Smartphone,
  Thermometer, Lock, Unlock, Bug, ExternalLink, Radio, Cpu, Eye, Monitor,
  Play, StopCircle, Download, FileText, PieChart, BarChart3, Globe, Zap
} from 'lucide-react';
import { iotScan as iotScanApi } from '../../api/client';
import { useI18n } from '../../i18n';

// Device type icons mapping
const getDeviceIcon = (deviceType) => {
  const iconMap = {
    'camera': Camera,
    'router': Router,
    'smart_home': Thermometer,
    'sensor': Radio,
    'gateway': Globe,
    'plc': Cpu,
    'hmi': Monitor,
    'smart_speaker': Radio,
    'smart_tv': Monitor,
    'thermostat': Thermometer,
    'light': Zap,
    'lock': Lock,
    'other': Server
  };
  return iconMap[deviceType?.toLowerCase()] || Server;
};

// Severity badge component
const SeverityBadge = ({ severity }) => {
  const colors = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/50',
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
    low: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    info: 'bg-gray-500/20 text-gray-400 border-gray-500/50'
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${colors[severity?.toLowerCase()] || colors.info}`}>
      {severity?.toUpperCase() || 'UNKNOWN'}
    </span>
  );
};

// Risk level badge
const RiskBadge = ({ level }) => {
  const colors = {
    critical: 'bg-red-600 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-yellow-500 text-black',
    low: 'bg-green-500 text-white',
    unknown: 'bg-gray-500 text-white'
  };

  return (
    <span className={`px-3 py-1 text-sm font-bold rounded ${colors[level?.toLowerCase()] || colors.unknown}`}>
      {level?.toUpperCase() || 'UNKNOWN'}
    </span>
  );
};

// Status badge
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { color: 'bg-gray-500/20 text-gray-400', icon: Clock },
    running: { color: 'bg-blue-500/20 text-blue-400', icon: Activity },
    completed: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    failed: { color: 'bg-red-500/20 text-red-400', icon: XCircle },
    stopped: { color: 'bg-yellow-500/20 text-yellow-400', icon: StopCircle }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`flex items-center gap-2 px-3 py-1 rounded-full ${config.color}`}>
      <Icon className="w-4 h-4" />
      {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'}
    </span>
  );
};

// Progress bar component
const ProgressBar = ({ progress }) => (
  <div className="w-full bg-gray-700 rounded-full h-2">
    <div
      className="bg-gradient-to-r from-brand-green to-emerald-400 h-2 rounded-full transition-all duration-500"
      style={{ width: `${progress || 0}%` }}
    />
  </div>
);

// Stats card component
const StatsCard = ({ icon: Icon, label, value, color = 'text-brand-green' }) => (
  <div className="bg-brand-darker/50 rounded-lg p-4 border border-gray-700/50">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg bg-gray-800 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-gray-400 text-sm">{label}</p>
        <p className="text-white text-xl font-bold">{value}</p>
      </div>
    </div>
  </div>
);

// Device card component
const DeviceCard = ({ device }) => {
  const DeviceIcon = getDeviceIcon(device.device_type);

  return (
    <div className="bg-brand-darker/50 rounded-lg p-4 border border-gray-700/50 hover:border-brand-green/30 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${device.is_vulnerable ? 'bg-red-500/20 text-red-400' : 'bg-gray-800 text-gray-400'}`}>
            <DeviceIcon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-white font-medium">{device.ip_address}</p>
            <p className="text-gray-400 text-sm capitalize">{device.device_type || 'Unknown device'}</p>
          </div>
        </div>
        {device.is_vulnerable && (
          <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded-full">Vulnerable</span>
        )}
      </div>

      {device.vendor && (
        <div className="mt-3 pt-3 border-t border-gray-700/50">
          <p className="text-gray-400 text-sm">
            <span className="text-gray-500">Vendor:</span> {device.vendor}
            {device.model && <span className="ml-2">({device.model})</span>}
          </p>
        </div>
      )}

      {device.protocols_detected?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {device.protocols_detected.map((proto, idx) => (
            <span key={idx} className="px-2 py-0.5 text-xs bg-gray-700 text-gray-300 rounded">
              {proto}
            </span>
          ))}
        </div>
      )}

      {device.open_ports?.length > 0 && (
        <div className="mt-2 text-sm text-gray-400">
          <span className="text-gray-500">Open ports:</span> {device.open_ports.join(', ')}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        {device.default_credentials && (
          <span className="flex items-center gap-1 px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">
            <Unlock className="w-3 h-3" /> Default creds
          </span>
        )}
        <RiskBadge level={device.risk_level} />
      </div>
    </div>
  );
};

// Vulnerability card component
const VulnerabilityCard = ({ vulnerability }) => (
  <div className="bg-brand-darker/50 rounded-lg p-4 border border-gray-700/50 hover:border-red-500/30 transition-all">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <SeverityBadge severity={vulnerability.severity} />
          {vulnerability.exploitable && (
            <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded-full border border-purple-500/50">
              Exploitable
            </span>
          )}
        </div>
        <h4 className="text-white font-medium">{vulnerability.title}</h4>
        <p className="text-gray-400 text-sm mt-1 line-clamp-2">{vulnerability.description}</p>
      </div>
      {vulnerability.cvss_score && (
        <div className="ml-4 text-center">
          <div className={`text-2xl font-bold ${
            vulnerability.cvss_score >= 9 ? 'text-red-400' :
            vulnerability.cvss_score >= 7 ? 'text-orange-400' :
            vulnerability.cvss_score >= 4 ? 'text-yellow-400' : 'text-green-400'
          }`}>
            {vulnerability.cvss_score.toFixed(1)}
          </div>
          <div className="text-xs text-gray-500">CVSS</div>
        </div>
      )}
    </div>

    <div className="mt-3 pt-3 border-t border-gray-700/50 grid grid-cols-2 gap-2 text-sm">
      <div>
        <span className="text-gray-500">Target:</span>
        <span className="text-gray-300 ml-1">{vulnerability.affected_device_ip}</span>
        {vulnerability.affected_port && (
          <span className="text-gray-500">:{vulnerability.affected_port}</span>
        )}
      </div>
      <div>
        <span className="text-gray-500">Protocol:</span>
        <span className="text-gray-300 ml-1">{vulnerability.affected_protocol || 'N/A'}</span>
      </div>
    </div>

    {vulnerability.mitigation && (
      <div className="mt-3 p-2 bg-green-500/10 rounded border border-green-500/20">
        <p className="text-green-400 text-sm">
          <span className="font-medium">Mitigation:</span> {vulnerability.mitigation}
        </p>
      </div>
    )}
  </div>
);

// Main component
const IoTScanDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshInterval, setRefreshInterval] = useState(null);

  const fetchScan = useCallback(async () => {
    try {
      const data = await iotScanApi.get(id);
      setScan(data);
      setError(null);

      // Stop auto-refresh if scan is completed or failed
      if (data.status === 'completed' || data.status === 'failed' || data.status === 'stopped') {
        if (refreshInterval) {
          clearInterval(refreshInterval);
          setRefreshInterval(null);
        }
      }
    } catch (err) {
      console.error('Error fetching IoT scan:', err);
      setError(err.response?.data?.detail || 'Failed to load scan details');
    } finally {
      setLoading(false);
    }
  }, [id, refreshInterval]);

  useEffect(() => {
    fetchScan();

    // Auto-refresh for running scans
    const interval = setInterval(() => {
      if (scan?.status === 'running' || scan?.status === 'pending') {
        fetchScan();
      }
    }, 5000);

    setRefreshInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [id]);

  const handleStopScan = async () => {
    try {
      await iotScanApi.stop(id);
      fetchScan();
    } catch (err) {
      console.error('Error stopping scan:', err);
    }
  };

  const handleDeleteScan = async () => {
    if (window.confirm('Are you sure you want to delete this IoT scan?')) {
      try {
        await iotScanApi.delete(id);
        navigate('/scans');
      } catch (err) {
        console.error('Error deleting scan:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black p-6 flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 text-brand-green animate-spin mx-auto" />
          <p className="text-gray-400 mt-4">Loading IoT scan details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-brand-black p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/scans')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Scans
          </button>

          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-6 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl text-red-400 font-medium">{error}</h2>
            <p className="text-gray-400 mt-2">The IoT scan could not be loaded.</p>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: PieChart },
    { id: 'devices', label: `Devices (${scan?.devices?.length || 0})`, icon: Router },
    { id: 'vulnerabilities', label: `Vulnerabilities (${scan?.vulnerabilities?.length || 0})`, icon: Bug }
  ];

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/scans')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <Wifi className="w-6 h-6 text-brand-green" />
                <h1 className="text-2xl font-bold text-white">IoT Scan Details</h1>
                <StatusBadge status={scan?.status} />
              </div>
              <p className="text-gray-400 mt-1 flex items-center gap-2">
                <Target className="w-4 h-4" />
                {scan?.target}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchScan}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCcw className="w-5 h-5" />
            </button>
            {scan?.status === 'running' && (
              <button
                onClick={handleStopScan}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded-lg transition-colors"
              >
                <StopCircle className="w-4 h-4" />
                Stop
              </button>
            )}
            <button
              onClick={handleDeleteScan}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>

        {/* Progress bar for running scans */}
        {(scan?.status === 'running' || scan?.status === 'pending') && (
          <div className="mb-6 bg-brand-darker rounded-lg p-4 border border-gray-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400">Scan Progress</span>
              <span className="text-brand-green font-medium">{scan?.progress || 0}%</span>
            </div>
            <ProgressBar progress={scan?.progress} />
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <StatsCard icon={Router} label="Appareils trouvés" value={scan?.devices_found || 0} color="text-blue-400" />
          <StatsCard icon={Bug} label="Vulnérabilités" value={scan?.vulnerabilities_found || 0} color="text-red-400" />
          <StatsCard icon={AlertTriangle} label="Critiques" value={scan?.critical_count || 0} color="text-red-500" />
          <StatsCard icon={AlertTriangle} label="Élevées" value={scan?.high_count || 0} color="text-orange-400" />
          <StatsCard icon={Info} label="Moyennes" value={scan?.medium_count || 0} color="text-yellow-400" />
          <StatsCard icon={Info} label="Basses" value={scan?.low_count || 0} color="text-blue-400" />
        </div>

        {/* Risk Score */}
        {scan?.risk_score !== undefined && (
          <div className="mb-6 bg-brand-darker rounded-lg p-6 border border-gray-700/50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-white">Risk Assessment</h3>
                <p className="text-gray-400 text-sm">Overall security risk based on discovered vulnerabilities</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-3xl font-bold text-white">{scan.risk_score}/100</p>
                  <p className="text-gray-400 text-sm">Risk Score</p>
                </div>
                <RiskBadge level={scan.risk_level} />
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-700/50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-brand-green border-b-2 border-brand-green'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Scan Information */}
              <div className="bg-brand-darker rounded-lg p-6 border border-gray-700/50">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5 text-brand-green" />
                  Scan Information
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Target</span>
                    <span className="text-white">{scan?.target}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Scan Type</span>
                    <span className="text-white capitalize">{scan?.scan_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Protocols</span>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {scan?.protocols?.map((proto, idx) => (
                        <span key={idx} className="px-2 py-0.5 text-xs bg-gray-700 text-gray-300 rounded">
                          {proto}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Created</span>
                    <span className="text-white">{new Date(scan?.created_at).toLocaleString()}</span>
                  </div>
                  {scan?.started_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Started</span>
                      <span className="text-white">{new Date(scan.started_at).toLocaleString()}</span>
                    </div>
                  )}
                  {scan?.completed_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Completed</span>
                      <span className="text-white">{new Date(scan.completed_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Device Types Distribution */}
              <div className="bg-brand-darker rounded-lg p-6 border border-gray-700/50">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-brand-green" />
                  Device Types
                </h3>
                {scan?.devices?.length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(
                      scan.devices.reduce((acc, device) => {
                        const type = device.device_type || 'other';
                        acc[type] = (acc[type] || 0) + 1;
                        return acc;
                      }, {})
                    ).map(([type, count]) => {
                      const Icon = getDeviceIcon(type);
                      return (
                        <div key={type} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-300 capitalize">{type}</span>
                          </div>
                          <span className="text-white font-medium">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No devices found</p>
                )}
              </div>

              {/* Vulnerability Distribution */}
              <div className="bg-brand-darker rounded-lg p-6 border border-gray-700/50 lg:col-span-2">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-brand-green" />
                  Distribution des vulnérabilités par sévérité
                </h3>
                <div className="grid grid-cols-5 gap-4">
                  {['critical', 'high', 'medium', 'low', 'info'].map(severity => {
                    const count = scan?.vulnerabilities?.filter(v => v.severity?.toLowerCase() === severity).length || 0;
                    const colors = {
                      critical: 'bg-red-500',
                      high: 'bg-orange-500',
                      medium: 'bg-yellow-500',
                      low: 'bg-blue-500',
                      info: 'bg-gray-500'
                    };
                    const severityLabels = {
                      critical: 'Critique',
                      high: 'Élevée',
                      medium: 'Moyenne',
                      low: 'Basse',
                      info: 'Info'
                    };
                    const maxCount = Math.max(...['critical', 'high', 'medium', 'low', 'info'].map(s =>
                      scan?.vulnerabilities?.filter(v => v.severity?.toLowerCase() === s).length || 0
                    ), 1);

                    return (
                      <div key={severity} className="text-center">
                        <div className="h-32 flex flex-col justify-end mb-2">
                          <div
                            className={`${colors[severity]} rounded-t-lg transition-all duration-500`}
                            style={{ height: `${(count / maxCount) * 100}%`, minHeight: count > 0 ? '20px' : '0' }}
                          />
                        </div>
                        <p className="text-2xl font-bold text-white">{count}</p>
                        <p className="text-gray-400 text-sm">{severityLabels[severity]}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Devices Tab */}
          {activeTab === 'devices' && (
            <div>
              {scan?.devices?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {scan.devices.map((device, idx) => (
                    <DeviceCard key={device.id || idx} device={device} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-brand-darker rounded-lg border border-gray-700/50">
                  <Router className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg text-gray-400">No IoT Devices Found</h3>
                  <p className="text-gray-500 mt-2">The scan did not discover any IoT devices on the target network.</p>
                </div>
              )}
            </div>
          )}

          {/* Vulnerabilities Tab */}
          {activeTab === 'vulnerabilities' && (
            <div>
              {scan?.vulnerabilities?.length > 0 ? (
                <div className="space-y-4">
                  {scan.vulnerabilities.map((vuln, idx) => (
                    <VulnerabilityCard key={vuln.id || idx} vulnerability={vuln} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-brand-darker rounded-lg border border-gray-700/50">
                  <Shield className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg text-gray-400">No Vulnerabilities Found</h3>
                  <p className="text-gray-500 mt-2">The scan did not identify any security vulnerabilities.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IoTScanDetails;
